import { Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationError, Router } from '@angular/router';
import { filter } from 'rxjs';
import { ToastService } from './toast.service';

@Injectable({ providedIn: 'root' })
export class NetworkService {
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);

  readonly isOnline = signal<boolean>(navigator.onLine);
  private shouldReloadOnConnect = false;

  constructor() {
    this.initNetworkListeners();
    this.initRouterErrorListener();
  }

  private initNetworkListeners() {
    window.addEventListener('online', () => {
      this.isOnline.set(true);
      this.toast.showSuccess(
        'NETWORK.RECONNECTED_TITLE',
        'NETWORK.RECONNECTED_MSG',
      );

      if (this.shouldReloadOnConnect) {
        this.shouldReloadOnConnect = false;
        window.location.reload();
      }
    });

    window.addEventListener('offline', () => {
      this.isOnline.set(false);
      this.toast.showWarning(
        'NETWORK.DISCONNECTED_TITLE',
        'NETWORK.DISCONNECTED_MSG',
      );
      this.shouldReloadOnConnect = true;
    });
  }

  private initRouterErrorListener() {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationError => event instanceof NavigationError,
        ),
        takeUntilDestroyed(),
      )
      .subscribe((event: NavigationError) => {
        const error = event.error;
        const errorMsg = error?.message || '';
        const isChunkError =
          errorMsg.includes('Loading chunk') ||
          errorMsg.includes('ChunkLoadError') ||
          errorMsg.includes('Failed to fetch dynamically imported module') ||
          error?.name === 'ChunkLoadError';

        // Nếu xảy ra lỗi điều hướng khi đang ngoại tuyến HOẶC là lỗi tải chunk file khi trực tuyến
        if (!navigator.onLine || isChunkError) {
          this.handleChunkLoadError();
        }
      });
  }

  private handleChunkLoadError() {
    if (!navigator.onLine) {
      this.toast.showError(
        'NETWORK.LOAD_ERROR_TITLE',
        'NETWORK.LOAD_ERROR_MSG',
      );
      this.shouldReloadOnConnect = true;
    } else {
      // Nếu trực tuyến mà lỗi tải chunk (do deploy phiên bản mới) -> reload để lấy code mới nhất.
      window.location.reload();
    }
  }
}
